/**
 * Stuck-stamp resolver.
 *
 * The server now creates pre-approved `stamp_requests` rows with a NULL
 * `customer_shop_account_id` / `customer_profile_id` (scan-stamp before the
 * customer's first tap, trade-in, migration). The customer-side sweep
 * (`recordOnChainInner` in Dashboard.tsx) can't sign `record_purchase`
 * without those ids — this module fills them in from live chain state,
 * minting the CustomerProfile / CustomerShopAccount if the customer
 * genuinely doesn't have one yet.
 *
 * Pure service layer: no React/UI imports. All chain calls are injected via
 * `deps` so the resolution logic is unit-testable without a real signer.
 */
import type { StampRequestRow } from './stamp-service';
import {
  fetchCustomerShopAccounts,
  fetchCustomerProfile,
  buildRegisterCustomerTx,
  buildCreateShopAccountTx,
} from './sui-service';
import { signAndExecuteTransaction } from './zklogin-signer';
import { addressesEqual } from '../lib/shop-address';

export interface ResolvedStampIds {
  accountId: string;
  profileId: string;
}

export interface ResolverDeps {
  fetchCustomerShopAccounts: typeof fetchCustomerShopAccounts;
  fetchCustomerProfile: typeof fetchCustomerProfile;
  buildRegisterCustomerTx: typeof buildRegisterCustomerTx;
  buildCreateShopAccountTx: typeof buildCreateShopAccountTx;
  signAndExecuteTransaction: typeof signAndExecuteTransaction;
}

export const defaultResolverDeps: ResolverDeps = {
  fetchCustomerShopAccounts,
  fetchCustomerProfile,
  buildRegisterCustomerTx,
  buildCreateShopAccountTx,
  signAndExecuteTransaction,
};

/** Pull the CustomerProfile object id out of a `fetchCustomerProfile` result, or null. */
function extractProfileId(profileObj: any): string | null {
  return profileObj?.data?.objectId ?? null;
}

/**
 * Find the owned CustomerShopAccount object matching `shopOwnerAddress` among
 * a `fetchCustomerShopAccounts` result. The CSA's on-chain `shop` field is the
 * shop owner's ADDRESS, not the shop object id — match with `addressesEqual`,
 * never `===` (documented gotcha; see src/lib/shop-address.ts).
 */
function findMatchingAccountId(accounts: any[] | null | undefined, shopOwnerAddress: string): string | null {
  const match = (accounts || []).find((a: any) =>
    addressesEqual(a?.data?.content?.fields?.shop, shopOwnerAddress),
  );
  return match?.data?.objectId ?? null;
}

/**
 * Mint a CustomerProfile for a customer who somehow reached the sweep without
 * one (mirrors the auto-register step in Dashboard's loadData). Small and
 * deps-injected so it's cheap to unit-test in isolation from the rest of the
 * resolver.
 */
export async function mintCustomerProfile(
  userAddress: string,
  deps: Pick<ResolverDeps, 'buildRegisterCustomerTx' | 'signAndExecuteTransaction' | 'fetchCustomerProfile'>,
): Promise<string | null> {
  const tx = deps.buildRegisterCustomerTx('');
  await deps.signAndExecuteTransaction(tx);
  const refetched = await deps.fetchCustomerProfile(userAddress);
  return extractProfileId(refetched);
}

/**
 * Mint a CustomerShopAccount for `shopOwnerAddress`, mirroring the sequencing
 * in Dashboard's `addNewShopToState` first-claim flow: `create_shop_account`
 * takes the CustomerProfile object id (not the raw address) plus the shop
 * owner's address. Small and deps-injected so it's cheap to unit-test in
 * isolation from the rest of the resolver.
 */
export async function mintCustomerShopAccount(
  userAddress: string,
  profileId: string,
  shopOwnerAddress: string,
  deps: Pick<ResolverDeps, 'buildCreateShopAccountTx' | 'signAndExecuteTransaction' | 'fetchCustomerShopAccounts'>,
): Promise<string | null> {
  const tx = deps.buildCreateShopAccountTx(profileId, shopOwnerAddress);
  await deps.signAndExecuteTransaction(tx);
  const refetched = await deps.fetchCustomerShopAccounts(userAddress);
  return findMatchingAccountId(refetched, shopOwnerAddress);
}

/**
 * Resolve (or mint) the `customer_shop_account_id` / `customer_profile_id`
 * a stuck stamp row is missing.
 *
 * ① Row already has both ids → return them as-is, no chain calls.
 * ② Live CSA + profile already exist on-chain → return their ids, no mint.
 * ③ Missing profile and/or CSA → mint whatever's absent (profile first, since
 *    `create_shop_account` needs the profile's object id — same sequencing
 *    as the existing first-claim flow), then return the fresh ids.
 * ④ Any failure (fetch/sign/mint throws, or a mint doesn't yield a matching
 *    object) → null. Caller shows "MISSING ACCOUNT DATA — REFRESH & RETRY".
 */
export async function resolveStampIds(
  row: StampRequestRow,
  userAddress: string,
  deps: ResolverDeps,
): Promise<ResolvedStampIds | null> {
  if (row.customer_shop_account_id && row.customer_profile_id) {
    return { accountId: row.customer_shop_account_id, profileId: row.customer_profile_id };
  }

  try {
    const [profileObj, accounts] = await Promise.all([
      deps.fetchCustomerProfile(userAddress),
      deps.fetchCustomerShopAccounts(userAddress),
    ]);

    let profileId = extractProfileId(profileObj);
    let accountId = findMatchingAccountId(accounts, row.shop_owner_address);

    if (accountId && profileId) {
      return { accountId, profileId };
    }

    if (!profileId) {
      profileId = await mintCustomerProfile(userAddress, deps);
      if (!profileId) return null;
    }

    if (!accountId) {
      accountId = await mintCustomerShopAccount(userAddress, profileId, row.shop_owner_address, deps);
      if (!accountId) return null;
    }

    return { accountId, profileId };
  } catch (err) {
    console.warn('[stuck-stamp-resolver] resolveStampIds failed:', err);
    return null;
  }
}
